import React from 'react';
import { CatalogCategory, CatalogCompany, CatalogCompanyType } from '@/shared/types/catalog';
import { getSupplierCapabilities, getSupplierName, getVerticalName } from '@/shared/utils/sourcing-marketplace-util';
import { VerticalName } from '@/shared/types/category';
import { SupplierProfilePdfListItem } from '@/shared/types/sourcing-marketplace';
import { ATTRIBUTE_PALETTE, DEFAULT_PASTEL_SHADE } from '@/shared/constants/supplier-attributes';
import NotablePastProjects from '../NotablePastProjects';
import './index.less';
import { isEmpty, map, uniq, upperFirst } from 'lodash-es';
import SupplierStats from '../SupplierStats';

interface IProps {
  isMobile: boolean;
  supplier: CatalogCompany;
  categoryList: CatalogCategory[];
  onReviewLabelClick?: () => void;
}

const CapabilitiesTab = ({ isMobile, supplier, categoryList, onReviewLabelClick }: IProps) => {
  const isServices = supplier?.type === CatalogCompanyType.SPECIALIST;
  const supplierType = getVerticalName(
    isServices ? VerticalName.SERVICES : VerticalName.PRODUCTS,
    /* isSingular */ true,
  );
  const noteAttributes = supplier?.noteAttributes || [];
  const capabilities: string[] = getSupplierCapabilities(supplier, isServices);
  const catalogUrls: SupplierProfilePdfListItem[] = supplier?.catalogUrls || [];

  const supplierSearchKeywords = supplier?.searchKeywords ?? [];
  const categorySubgroups = categoryList.map((cat) => cat.subgroup);
  const tags = uniq([...supplierSearchKeywords, ...categorySubgroups]);

  const supplierName = getSupplierName(supplier);

  return (
    <div className="capabilities-cards-box">
      {isMobile && <SupplierStats supplier={supplier} onReviewLabelClick={onReviewLabelClick} />}
      <div className="capabilities-card">
        <h2 className="title bold">{supplierName} is known for</h2>
        {!isEmpty(noteAttributes) && (
          <div className="tags">
            {noteAttributes.map((attr: string) => (
              <span
                className="tag"
                key={attr}
                style={{ backgroundColor: ATTRIBUTE_PALETTE[attr] || DEFAULT_PASTEL_SHADE }}
              >
                {attr}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="capabilities-card">
        <h2 className="title bold">Ask {supplierName} about</h2>
        {!isEmpty(capabilities) && (
          <ul className="tags">
            {capabilities.map((capability: string) => (
              <li className="tag" key={capability}>
                {capability}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="capabilities-card">
        <h2 className="title bold">
          {supplierName} {isServices ? 'offers' : 'can make'}
        </h2>
        <ul className="tags">
          {map(tags, (tag: string) => {
            return (
              <li className="tag" key={tag}>
                {tag}
              </li>
            );
          })}
        </ul>
      </div>
      {!isEmpty(catalogUrls) && (
        <div className="certificates">
          <h2 className="title">{upperFirst(supplierType)} Certificates & Documents</h2>
          <div className="catalog-items">
            {catalogUrls.map((item: SupplierProfilePdfListItem, i: number) => (
              <div className="catalog-item-pdf" key={i}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/images/sourcing/download.svg" width={14} height={14} alt="download" />
                <a href={item.image} download={`${supplierName} - Company Profile`} target="_blank" rel="noreferrer">
                  Download ({item.title})
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
      {isMobile && <NotablePastProjects supplier={supplier} />}
    </div>
  );
};

export default CapabilitiesTab;
